import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { isNativePlatform, getPlatform } from '@/utils/nativePlugins';

// Root routes where back button should exit the app
const EXIT_ROUTES = [
  '/buyer/dashboard',
  '/seller/dashboard',
  '/native/role-select',
  '/'
];

/**
 * Hook that handles Android native back button navigation.
 * Uses React Router navigation instead of exiting the app immediately.
 * Exits app only when on root/dashboard pages.
 */
export const useAndroidBackButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const historyStack = useRef<string[]>([]);

  // Track navigation history
  useEffect(() => {
    historyStack.current.push(location.pathname);
    // Keep only last 50 entries to prevent memory issues
    if (historyStack.current.length > 50) {
      historyStack.current.shift();
    }
  }, [location.pathname]);

  useEffect(() => {
    // Only activate on native Android
    if (!isNativePlatform() || getPlatform() !== 'android') {
      return;
    }

    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const setupListener = async () => {
      listenerHandle = await App.addListener('backButton', ({ canGoBack }) => {
        const currentPath = location.pathname;
        
        // If on a root/home route, exit the app
        if (EXIT_ROUTES.includes(currentPath)) {
          App.exitApp();
          return;
        }

        // Check if we have history to go back
        if (historyStack.current.length > 1) {
          navigate(-1); // Go back in React Router history
        } else if (canGoBack) {
          window.history.back();
        } else {
          App.exitApp();
        }
      });
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate, location.pathname]);
};
